import React, { useState, useEffect, FC } from "react";
import Content from "../../layout/content/Content";
import { Card } from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
} from "../../components/Component";
import { Steps, Step } from "react-step-builder";
import { useForm, FormProvider } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { message, Spin, Flex } from "antd";
import dayjs from "dayjs";

// Import step components
import WorkRequestGeneralDetails from "./workRequestCreation/workRequestGeneralDetails";
import WorkRequestDetails from "./workRequestCreation/workRequestDetails";
import WorkRequestPreview from "./workRequestCreation/workRequestPreview";
import Header_WorkRequest from "./workRequestCreation/Header_WorkRequest";

// Import types and data
import {
  RequestForType,
  WorkRequestFormData,
  WorkRequestStatus,
} from "./types";
import {
  getWorkRequestById,
  generateWorkRequestNumber,
  getMasterData,
} from "./mockData/workRequestData";

const config = {
  before:
    Header_WorkRequest as React.ComponentType<{}> as () => React.ReactElement,
};

const WorkRequestCreationMain: FC = () => {
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  // Check if editing or duplicating existing work request
  const editId = searchParams.get("edit");
  const duplicateId = searchParams.get("duplicate");
  const isEditMode = !!editId;
  const isDuplicateMode = !!duplicateId;

  const formMethods = useForm<WorkRequestFormData>({
    mode: "onChange",
    defaultValues: {
      // General Information - Following FSD specs exactly
      workRequestNumber: "",
      workRequestTitle: "",
      description: "",
      siteId: "",
      siteName: "",
      projectId: "",
      projectName: "",
      requestFor: RequestForType.ConstructionWork,
      plannedStartDate: null,
      plannedEndDate: null,
      approvalRequired: "false", // String for form handling

      // Work Request Lines
      workRequestLines: [],

      // Internal fields
      status: WorkRequestStatus.Draft,
      type: isEditMode ? "edit" : isDuplicateMode ? "duplicate" : "create",
      createdBy: "Current User",
    },
  });

  const { watch, setValue, reset } = formMethods;
  const location = useLocation();

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);

    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  useEffect(() => {
    // Auto-generate Work Request Number if creating new (FSD requirement)
    if (!editId && !duplicateId) {
      generateAndSetWorkRequestNumber();
    }

    // Load existing data if editing or duplicating
    if (editId) {
      loadWorkRequestData(editId);
    } else if (duplicateId) {
      loadWorkRequestDataForDuplicate(duplicateId);
    }
  }, [editId, duplicateId]);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  // Auto-generate Work Request Number with FSD format
  const generateAndSetWorkRequestNumber = () => {
    const wrNumber = generateWorkRequestNumber();
    setValue("workRequestNumber", wrNumber);
  };

  const loadWorkRequestData = async (id: string) => {
    try {
      setLoading(true);
      const workRequest = getWorkRequestById(id);

      if (workRequest) {
        // Set form values from existing data
        setValue("workRequestNumber", workRequest.workRequestNumber);
        setValue("workRequestTitle", workRequest.workRequestTitle);
        setValue("description", workRequest.description || "");
        setValue("siteId", workRequest.siteId);
        setValue("siteName", workRequest.siteName);
        setValue("projectId", workRequest.projectId || "");
        setValue("projectName", workRequest.projectName || "");
        setValue("requestFor", workRequest.requestFor as RequestForType);
        setValue("plannedStartDate", "");
        setValue("plannedEndDate", "");
        setValue(
          "approvalRequired",
          workRequest.approvalRequired ? "true" : "false"
        );
        setValue("workRequestLines", []);
        setValue("status", workRequest.status as WorkRequestStatus);
        setValue("type", "edit");

        message.success("Work request data loaded successfully");
      } else {
        message.error("Work request not found");
      }
    } catch (error) {
      console.error("Error loading work request:", error);
      message.error("Failed to load work request data");
    } finally {
      setLoading(false);
    }
  };

  const loadWorkRequestDataForDuplicate = async (id: string) => {
    try {
      setLoading(true);
      const workRequest = getWorkRequestById(id);

      if (workRequest) {
        // Generate new number for duplicate
        generateAndSetWorkRequestNumber();

        // Copy data but reset status and IDs
        setValue("workRequestTitle", `Copy of ${workRequest.workRequestTitle}`);
        setValue("description", workRequest.description || "");
        setValue("siteId", workRequest.siteId);
        setValue("siteName", workRequest.siteName);
        setValue("projectId", workRequest.projectId || "");
        setValue("projectName", workRequest.projectName || "");
        setValue("requestFor", workRequest.requestFor as RequestForType);
        setValue("plannedStartDate", null); // Reset dates for duplicate
        setValue("plannedEndDate", null);
        setValue(
          "approvalRequired",
          workRequest.approvalRequired ? "true" : "false"
        );

        // Copy work lines but generate new IDs
        const copiedLines: any = workRequest.workLines.map((line) => ({
          ...line,
          id: `${Date.now()}-${Math.random()}`,
        }));
        setValue("workRequestLines", copiedLines);
        setValue("status", WorkRequestStatus.Draft);
        setValue("type", "duplicate");

        message.success("Work request duplicated successfully");
      } else {
        message.error("Work request not found");
      }
    } catch (error) {
      console.error("Error duplicating work request:", error);
      message.error("Failed to duplicate work request");
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (isEditMode) return `Edit Work Request - ${watch("workRequestNumber")}`;
    if (isDuplicateMode)
      return `Duplicate Work Request - ${watch("workRequestNumber")}`;
    return "New Work Request";
  };

  const getPageSubtitle = () => {
    if (isEditMode) return "Modify existing work request details";
    if (isDuplicateMode)
      return "Create new work request from existing template";
    return "Create a new work request following the three-step process";
  };

  return (
    <>
      <Head title="Work Request Creation" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}

              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">{getPageTitle()}</BlockTitle>
                    {/* <p className="text-muted mt-1">{getPageSubtitle()}</p> */}
                    {watch("workRequestNumber") && (
                      <div className="mt-2">
                        <span className="badge badge-outline-primary">
                          WR #: {watch("workRequestNumber")}
                        </span>
                        {watch("status") && (
                          <span className="badge badge-outline-info ms-2">
                            Status: {watch("status")}
                          </span>
                        )}
                      </div>
                    )}
                  </BlockHeadContent>

                  <BlockHeadContent>
                    <Link to="/work-request-list">
                      <Button
                        outline
                        className="bg-white d-none d-sm-inline-flex"
                      >
                        <Icon name="arrow-left"></Icon>
                        <span>Back to List</span>
                      </Button>
                      <Button
                        outline
                        className="btn-icon bg-white d-inline-flex d-sm-none"
                      >
                        <Icon name="arrow-left"></Icon>
                      </Button>
                    </Link>
                  </BlockHeadContent>

                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Block className="mt-1">
                {loading ? (
                  <Flex align="center" gap="middle" className="pageLoader">
                    <Spin size="large" className="m-auto" />
                    <span>Loading work request data...</span>
                  </Flex>
                ) : (
                  <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix">
                    <FormProvider {...formMethods}>
                      <Steps config={config}>
                        {/* Step 1: General Information (FSD Section 1) */}
                        <Step component={WorkRequestGeneralDetails} />

                        {/* Step 2: Work Request Details (FSD Section 2) */}
                        <Step component={WorkRequestDetails} />

                        {/* Step 3: Preview & Submit (FSD Section 3) */}
                        <Step component={WorkRequestPreview} />
                      </Steps>
                    </FormProvider>
                  </div>
                )}
              </Block>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default WorkRequestCreationMain;
